import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { hasPermissionUtil } from '../utils/permissions';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});
  const isDev = process.env.NODE_ENV !== 'production';

  const getStoredUser = useCallback(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }, []);

  // Timeout de emergência para garantir que o loading pare
  useEffect(() => {
    const emergencyTimeout = setTimeout(() => {
      setLoading(false);
    }, 20000); // 20 segundos máximo

    return () => clearTimeout(emergencyTimeout);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setLoading(false);
    setPermissions({});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const fetchEffectivePermissions = useCallback(async (userId) => {
    try {
      if (!userId) return;
      const url = getApiUrl(`users/${userId}/effective-permissions`);
      const response = await axios.get(url, {
        timeout: 8000
      });
      if (response.data && response.data.success) {
        const eff = response.data.data?.effective_permissions || {};
        setPermissions(eff);
      } else {
        if (isDev) console.warn('Falha ao carregar permissões efetivas');
        setPermissions({});
      }
    } catch (error) {
      if (isDev) console.error('Erro ao buscar permissões efetivas:', error);
      setPermissions({});
    }
  }, [isDev]);

  const validateToken = useCallback(async () => {
    try {
      const isTimeoutError = (err) => {
        const code = err && err.code;
        const msg = String(err && err.message || '').toLowerCase();
        return code === 'ECONNABORTED' || msg.includes('timeout');
      };
      const waitMs = (ms) => new Promise(resolve => setTimeout(resolve, ms));

      let urlMe = getApiUrl('auth/me');

      let response;
      try {
        response = await axios.get(urlMe, { timeout: 15000 });
      } catch (e) {
        if (isTimeoutError(e)) {
          await waitMs(350);
          response = await axios.get(urlMe, { timeout: 15000 });
        } else {
          throw e;
        }
      }
      
      if (response.data.success) {
        const meUser = response.data.user;
        setUser(meUser);
        // Carregar permissões efetivas
        try {
          await fetchEffectivePermissions(meUser?.id);
        } catch (e) {
          // já logado, permissões serão vazias em caso de falha
        }
      } else {
        logout();
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        logout();
        return;
      }
      if (isDev) {
        console.error('Erro na validação do token:', error);
        console.error('URL tentada:', getApiUrl('auth/me'));
      }
      try {
        const altUrl = `/api/auth/me`;
        const altResp = await axios.get(altUrl, { timeout: 15000 });
        if (altResp.data?.success) {
          const meUser = altResp.data.user;
          setUser(meUser);
          try { await fetchEffectivePermissions(meUser?.id); } catch {}
          setLoading(false);
          return;
        }
      } catch {}

      const cached = getStoredUser();
      if (cached) {
        setUser(cached);
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [fetchEffectivePermissions, getStoredUser, isDev, logout]);

  useEffect(() => {
    // Validar token automaticamente ao iniciar para evitar usuário inconsistente
    const token = localStorage.getItem('token');

    if (token) {
      // Configurar header para validação
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Não definir usuário aqui; aguardar validateToken confirmar
      validateToken();
    } else {
      // Sem token, finalizar loading
      setLoading(false);
    }
  }, [validateToken]);

  const login = async (email, password) => {
    try {
      const loginUrl = getApiUrl('auth/login');
      let response = await axios.post(loginUrl, {
        email,
        password
      }, { timeout: 15000 });

      if (response.data.success) {
        const { token, user } = response.data;
        
        // Salvar token e configurar axios
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        setUser(user);
        // Buscar permissões efetivas após login
        try {
          await fetchEffectivePermissions(user?.id);
        } catch (e) {
          // Ignorar erro aqui; permissões serão buscadas no próximo ciclo
        }
        return { success: true };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      if (isDev) {
        console.error('Erro no login:', error);
        console.error('URL tentada:', getApiUrl('auth/login'));
      }
      try {
        const altUrl = `/api/auth/login`;
        const altResp = await axios.post(altUrl, { email, password }, { timeout: 15000 });
        if (altResp.data?.success) {
          const { token, user } = altResp.data;
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          setUser(user);
          try { await fetchEffectivePermissions(user?.id); } catch {}
          return { success: true };
        }
        return { success: false, error: altResp.data?.error || 'Erro ao conectar com o servidor' };
      } catch {}
      
      if (error.response?.data?.error) {
        return { success: false, error: error.response.data.error };
      }
      
      return { success: false, error: 'Erro ao conectar com o servidor' };
    }
  };

  const hasPermission = useCallback((key, fallbackRoles = []) => {
    return hasPermissionUtil(permissions, user?.role || '', key, fallbackRoles);
  }, [permissions, user]);

  const value = {
    user,
    setUser,
    login,
    logout,
    loading,
    permissions,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
